import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Loader2,
  User,
  Edit2,
  Trash2,
  MapPin,
  Phone,
  Mail,
  Users,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { adminService, RegisteredUser } from "../../services/adminService";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { AdminPageTitle } from "@/project/jain_elibrary-admin/components/shared/AdminPageTitle";
import { AdminPaginationBar } from "@/project/jain_elibrary-admin/components/shared/AdminPaginationBar";
import { AdminEmptyState } from "@/project/jain_elibrary-admin/components/shared/AdminEmptyState";
import { AdminTableSkeleton } from "@/project/jain_elibrary-admin/components/shared/AdminTableSkeleton";

const UserRegistration = () => {
  const [users, setUsers] = useState<RegisteredUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RegisteredUser | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<RegisteredUser>>({});
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchUsers = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoading(true);
        const data = await adminService.getRegisteredUsers({
          page: page,
          page_size: pagination.pageSize,
          search: search,
          ordering: "id", // Order by id ASC as requested
        });
        setUsers(data?.results ?? []);
        setPagination(prev => ({
          ...prev,
          count: data?.count ?? 0,
          page: page,
        }));
      } catch (error: any) {
        console.error("Error fetching users:", error);
        toast.error(
          error?.response?.data?.detail ??
            "Failed to load user registrations. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this user profile?")) {
      return;
    }

    try {
      await adminService.deleteRegisteredUser(id);
      toast.success("User profile deleted successfully");
      fetchUsers(pagination.page, searchTerm);
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user profile");
    }
  };

  const handleEditClick = (user: RegisteredUser) => {
    setSelectedUser(user);
    setEditFormData({
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      phone: user.phone ?? user.phone_number ?? "",
      city: user.city ?? "",
      state: user.state ?? "",
      country: user.country ?? "",
      address: user.address ?? "",
      postal_code: user.postal_code ?? "",
      is_active: user.is_active !== false,
      download_limit: user.download_limit ?? 25,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) {
      return;
    }

    try {
      setIsUpdating(true);
      await adminService.updateRegisteredUser(selectedUser.id, {
        ...editFormData,
        download_limit: Math.max(
          0,
          Number.isFinite(Number(editFormData.download_limit))
            ? Number(editFormData.download_limit)
            : 25
        ),
      });
      toast.success("User profile updated successfully");
      setIsEditModalOpen(false);
      fetchUsers(pagination.page, searchTerm);
    } catch (error: any) {
      console.error("Error updating user:", error);
      toast.error(
        error?.response?.data?.phone?.[0] ?? "Failed to update user profile"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers(1, searchTerm);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: nextPageSize, page: 1 }));
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <AdminPageTitle
          title="User registration"
          description="Browse and maintain registered profiles, limits, and status."
          actions={
            <form
              onSubmit={handleSearch}
              className="flex w-full max-w-sm flex-wrap items-center gap-2 sm:w-auto sm:justify-end"
            >
              <div className="relative min-w-[12rem] flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search users…"
                  className="w-full pl-9"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  aria-label="Search users"
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                className="shrink-0 bg-[#533a1d] text-white hover:bg-[#533a1d]/90"
              >
                Search
              </Button>
            </form>
          }
        />

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          {loading && users.length === 0 ? (
            <AdminTableSkeleton rows={10} columns={9} className="border-0" />
          ) : null}
          {!(loading && users.length === 0) ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="w-16 px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Daily limit
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading && users.length > 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="border-t border-border bg-muted/20 px-4 py-2 text-center text-xs text-muted-foreground"
                      >
                        <span className="inline-flex items-center justify-center gap-2">
                          <Loader2
                            className="h-4 w-4 animate-spin"
                            aria-hidden
                          />
                          Updating list…
                        </span>
                      </td>
                    </tr>
                  ) : null}
                  {!loading && users?.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="p-6">
                        <AdminEmptyState
                          icon={Users}
                          title="No users found"
                          description="Adjust your search or invite users to register on the public site."
                        />
                      </td>
                    </tr>
                  ) : null}
                  {users?.length
                    ? users?.map(user => (
                        <tr
                          key={user.id}
                          className="transition-colors hover:bg-muted/30"
                        >
                          <td className="px-4 py-3 font-medium">{user.id}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span>
                                {(user.full_name ??
                                  `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()) ||
                                  "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">{user.email}</td>
                          <td className="px-4 py-3">
                            {user.phone_number ?? "-"}
                          </td>
                          <td className="px-4 py-3 tabular-nums">
                            {user.download_limit ?? 25}
                          </td>
                          <td className="px-4 py-3">
                            {[user.city, user.state, user.country]
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </td>
                          <td className="px-4 py-3 capitalize">
                            {user.user_type ?? "User"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                user.is_active !== false
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                              }`}
                            >
                              {user.is_active !== false ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-primary hover:bg-primary/50"
                                onClick={() => handleEditClick(user)}
                                title="Edit User Details"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:bg-destructive/80"
                                onClick={() => handleDelete(user.id)}
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        <AdminPaginationBar
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalCount={pagination.count}
          loading={loading}
          onPageChange={p => void fetchUsers(p, searchTerm)}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {/* Edit User Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl overflow-hidden border-none p-0 shadow-2xl">
          <DialogHeader className="border-b bg-muted/30 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <User className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold">
                  Edit User Details
                </DialogTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Update profile information for {selectedUser?.email}
                </p>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={handleUpdateUser}>
            <ScrollArea className="max-h-[70vh] px-6 py-6">
              <div className="space-y-6">
                {/* Basic Info Section */}
                <div>
                  <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" required>
                        First Name
                      </Label>
                      <Input
                        id="first_name"
                        value={editFormData.first_name}
                        onChange={e =>
                          setEditFormData({
                            ...editFormData,
                            first_name: e.target.value,
                          })
                        }
                        placeholder="John"
                        className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" required>
                        Last Name
                      </Label>
                      <Input
                        id="last_name"
                        value={editFormData.last_name}
                        onChange={e =>
                          setEditFormData({
                            ...editFormData,
                            last_name: e.target.value,
                          })
                        }
                        placeholder="Doe"
                        className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Info Section */}
                <div>
                  <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Contact & Status
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          value={editFormData.phone}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              phone: e.target.value,
                            })
                          }
                          placeholder="10 digit number"
                          className="border-muted-foreground/20 bg-muted/30 pl-10 transition-colors focus:bg-background"
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Account Status</Label>
                      <div className="flex items-center space-x-3 rounded-md border border-muted-foreground/10 bg-muted/30 p-2">
                        <Switch
                          id="is_active"
                          checked={editFormData.is_active}
                          onCheckedChange={checked =>
                            setEditFormData({
                              ...editFormData,
                              is_active: checked,
                            })
                          }
                        />
                        <Label
                          htmlFor="is_active"
                          className="cursor-pointer font-normal"
                        >
                          User account is{" "}
                          {editFormData.is_active ? "Active" : "Inactive"}
                        </Label>
                        <Badge
                          variant={
                            editFormData.is_active ? "default" : "secondary"
                          }
                          className="pointer-events-none ml-auto"
                        >
                          {editFormData.is_active ? "Active" : "Disabled"}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="download_limit">
                        Daily download limit
                      </Label>
                      <Input
                        id="download_limit"
                        type="number"
                        min={0}
                        step={1}
                        value={editFormData.download_limit ?? 25}
                        onChange={e => {
                          const raw = e.target.value;
                          const n = raw === "" ? 25 : parseInt(raw, 10);
                          setEditFormData({
                            ...editFormData,
                            download_limit: Number.isFinite(n) ? n : 25,
                          });
                        }}
                        className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Max distinct books per day (resets at midnight server
                        time). Use 0 for unlimited.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Location Section */}
                <div>
                  <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Location Details
                  </h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="address">Address</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="address"
                          value={editFormData.address}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              address: e.target.value,
                            })
                          }
                          placeholder="Street address"
                          className="border-muted-foreground/20 bg-muted/30 pl-10 transition-colors focus:bg-background"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          value={editFormData.city}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              city: e.target.value,
                            })
                          }
                          placeholder="City"
                          className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          value={editFormData.state}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              state: e.target.value,
                            })
                          }
                          placeholder="State"
                          className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">Country</Label>
                        <Input
                          id="country"
                          value={editFormData.country}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              country: e.target.value,
                            })
                          }
                          placeholder="Country"
                          className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>

            <DialogFooter className="items-center border-t bg-muted/30 px-6 py-4">
              <span className="mr-auto flex items-center gap-1 text-xs text-muted-foreground">
                <Mail className="h-3 w-3" /> {selectedUser?.email}
              </span>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUpdating}
                className="min-w-[100px]"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserRegistration;
